import { sanitizeHtml } from '@/features/tr-reportsettings/sanitizeHtml';
import type { RichTextConfig } from '@/core/models/richText';
import './applicationTestingText.scss';

/**
 * Application Testing descriptions and comments are written in the rich text editor and saved as HTML. Tickets and
 * comments raised before that are plain text (line breaks, maybe a pasted URL), so every value is run through
 * `toDisplayHtml`, which shows either kind well.
 */

const TAG = /<\/?[a-z][^>]*>/i;
const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Makes the web addresses in already-escaped plain text clickable. */
const linkify = (escaped: string) =>
  escaped.replace(/\bhttps?:\/\/[^\s<]+[^\s<.,;:!?)\]]/gi, (url) => `<a href="${url}">${url}</a>`);

/** Plain text keeps its line breaks and links; HTML is sanitised and its links open in a new tab. */
export function toDisplayHtml(value: string | null | undefined): string {
  const text = value ?? '';
  if (!text.trim()) return '';
  const html = TAG.test(text) ? sanitizeHtml(text) : linkify(escapeHtml(text)).replace(/\r?\n/g, '<br>');
  return html.replace(/<a /gi, '<a target="_blank" rel="noopener noreferrer" ');
}

/** The text a piece of rich text reads as: what the tag finder, the list and the "is it empty" checks work on. */
export function htmlToText(value: string | null | undefined): string {
  const text = value ?? '';
  if (!TAG.test(text)) return text;
  const container = document.createElement('div');
  container.innerHTML = sanitizeHtml(text.replace(/<(br|\/p|\/div|\/li|\/h[1-6])\s*\/?>/gi, '$&\n'));
  return (container.textContent ?? '').replace(/\u00a0/g, ' ');
}

/** True when nothing but empty markup was typed (an emptied editor leaves `<div><br></div>` behind). */
export const isRichTextEmpty = (value: string | null | undefined) =>
  htmlToText(value).trim().length === 0 && !/<(img|hr|video)\b/i.test(value ?? '');

/** The editor for ticket descriptions and comments: formatting, lists, links and the HTML view. No images or video, since
 *  those would be embedded in the text itself; screenshots go in as attachments. */
export const ticketEditorConfig = (placeholder: string, minHeight = '120'): RichTextConfig => ({
  editable: true,
  spellcheck: true,
  minHeight,
  maxHeight: '360',
  width: 'auto',
  minWidth: '0',
  translate: 'yes',
  enableToolbar: true,
  showToolbar: true,
  placeholder,
  defaultParagraphSeparator: '',
  // The saved HTML is sanitised without inline styles, so fonts, colours and alignment (all written as styles) would not
  // survive a save; they are left off rather than offered and then lost.
  toolbarHiddenButtons: [
    [
      'insertImage',
      'insertVideo',
      'fontName',
      'fontSize',
      'textColor',
      'backgroundColor',
      'justifyLeft',
      'justifyCenter',
      'justifyRight',
      'justifyFull',
    ],
  ],
});

/** A saved description or comment, formatted. */
export function RichTextView({ value, className = '' }: { value: string | null | undefined; className?: string }) {
  return (
    <div
      className={`application-testing-rich ${className}`.trim()}
      // sanitised by toDisplayHtml
      dangerouslySetInnerHTML={{ __html: toDisplayHtml(value) }}
    />
  );
}
